import useNonNullContext from '../../../../../../Hooks/useNonNullContext';
import { TiktokAudiosTableRowV2 } from '../../../../TikTok.types';
import { useCallback, useEffect, useMemo } from 'react';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import { CollectionsContext } from './CollectionsContext';
import {
    ClientMonitoredTiktokAudioRelationship,
    getClientMonitoredTiktokAudioRelationships,
    getTiktokAudioCollectionAudioStats,
    GetTiktokAudioCollectionAudioStatsParams,
    getTiktokAudioAssets,
} from '@round/api';

type CollectionAudiosValues = {
    rows: TiktokAudiosTableRowV2[];
    loading: boolean;
    errorLoading: boolean;
    count: number;
    page: number;
    pageSize: number;
    search: string;
    ordering: GetTiktokAudioCollectionAudioStatsParams['ordering'];
    audioIdForModal: number;
};

type CollectionAudiosUtils = {
    setPage: (page: number) => void;
    setPageSize: (pageSize: number | undefined) => void;
    setSearch: (query: string) => void;
    setOrdering: (order: GetTiktokAudioCollectionAudioStatsParams['ordering']) => void;
    setAudioIdForModal: (id: number | undefined) => void;
    setAlternateName: (rel: ClientMonitoredTiktokAudioRelationship) => void;
    refresh: (collectionId: number) => Promise<void>;
};

const useCollectionAudios = (): [CollectionAudiosValues, CollectionAudiosUtils] => {
    const [
        {
            selectedCollection,
            urlState,
            collectionAudioStatsCount,
            collectionAudioStats,
            errorLoadingCollectionAudioStats,
            collectionAudioStatsLoading,
            audioAssets,
            collectionClientMonitoredAudioRelations,
            collectionClientMonitoredAudioRelationsLoading,
        },
        { setUrlState, dispatch },
    ] = useNonNullContext(CollectionsContext);

    const page = Number(urlState.aP ?? 1);
    const pageSize = Number(urlState.aPS ?? 10);
    const search = urlState.aS ?? '';
    const ordering = urlState.aO as GetTiktokAudioCollectionAudioStatsParams['ordering'];
    const audioIdForModal = Number(urlState.audioModal);

    const fetchTiktokAudioStats = useCallback(
        async (collectionId: number, requestInit?: RequestInit) => {
            try {
                dispatch({ type: 'loadCollectionAudioStats' });
                const response = await getTiktokAudioCollectionAudioStats(
                    collectionId,
                    {
                        page,
                        page_size: pageSize,
                        search,
                        ordering: ordering ?? '-video_count_daily_change',
                    },
                    requestInit
                );

                dispatch({ type: 'loadedCollectionAudioStats', payload: response.data });
                return response.data;
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                dispatch({ type: 'errorLoadingCollectionAudioStats' });
            }
        },
        [ordering, page, pageSize, search, dispatch]
    );

    const fetchAssets = useCallback(
        async (ids: number[], requestInit?: RequestInit) => {
            try {
                const assets = await getTiktokAudioAssets(ids, requestInit);
                dispatch({ type: 'loadedAudioAssets', payload: assets });
            } catch {
                // noop, it will fallback to default image
            }
        },
        [dispatch]
    );

    const fetchClientMonitoredAudioRelations = useCallback(
        async (ids: number[], requestInit?: RequestInit) => {
            if (!ids.length) {
                return;
            }

            try {
                dispatch({ type: 'loadClientMonitoredAudioRelations' });
                const response = await getClientMonitoredTiktokAudioRelationships(
                    {
                        page: 1,
                        page_size: ids.length,
                        monitored_audio_id: ids.toString(),
                    },
                    requestInit
                );

                if (response.status === 200) {
                    dispatch({ type: 'loadedClientMonitoredAudioRelations', payload: response.data.results });
                }
            } catch {
                dispatch({ type: 'errorLoadingClientMonitoredAudioRelations' });
            }
        },
        [dispatch]
    );

    const refresh = useCallback(
        async (collectionId: number) => {
            const response = await fetchTiktokAudioStats(collectionId);
            if (response) {
                await fetchAssets(response.results.map((s) => s.music_id));
            }
        },
        [fetchAssets, fetchTiktokAudioStats]
    );

    const rows: TiktokAudiosTableRowV2[] = useMemo(
        () =>
            collectionAudioStats.map((stats) => {
                const asset = audioAssets.find((asset) => asset.audio_id === stats.music_id);
                const rel = collectionClientMonitoredAudioRelations.filter(
                    (rel) =>
                        rel.monitored_audio_id === stats.monitored_music_id &&
                        rel.client_id === selectedCollection?.client
                );

                return {
                    ...stats,
                    image_url: asset?.image_thumb.cached_url || asset?.image_thumb.original_url,
                    audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url,
                    clientMonitoredAudioRelation: rel,
                    dailyGrowth: stats.standardised_data.daily_video_counts.slice(-7).reduce(
                        (acc, current) => {
                            acc.push({
                                timestamp: current.timestamp,
                                value: current.change,
                            });

                            return acc;
                        },
                        [] as TiktokAudiosTableRowV2['dailyGrowth']
                    ),
                };
            }),
        [audioAssets, collectionAudioStats, collectionClientMonitoredAudioRelations, selectedCollection?.client]
    );

    useEffect(() => {
        if (selectedCollection?.id) {
            dispatch({ type: 'resetAudios' });
        }
    }, [dispatch, selectedCollection?.id]);

    useAbortableEffect(
        (signal) => {
            if (selectedCollection) {
                fetchTiktokAudioStats(selectedCollection.id, { signal });
            }
        },
        [selectedCollection, fetchTiktokAudioStats]
    );

    useAbortableEffect(
        (signal) => {
            if (collectionAudioStats.length) {
                fetchAssets(
                    collectionAudioStats.map((s) => s.music_id),
                    { signal }
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [collectionAudioStats.map((s) => s.music_id).join(), collectionAudioStats.length, fetchAssets]
    );

    useAbortableEffect(
        (signal) => {
            if (collectionAudioStats.length) {
                fetchClientMonitoredAudioRelations(
                    collectionAudioStats.map((s) => s.monitored_music_id).filter((id): id is number => id !== null),
                    { signal }
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            // eslint-disable-next-line react-hooks/exhaustive-deps
            collectionAudioStats.map((s) => s.monitored_music_id).join(),
            collectionAudioStats.length,
            fetchClientMonitoredAudioRelations,
        ]
    );

    const values: CollectionAudiosValues = useMemo(
        () => ({
            page,
            audioIdForModal,
            ordering,
            pageSize,
            search,
            rows,
            count: collectionAudioStatsCount,
            loading: collectionAudioStatsLoading || collectionClientMonitoredAudioRelationsLoading,
            errorLoading: errorLoadingCollectionAudioStats,
        }),
        [
            audioIdForModal,
            collectionAudioStatsCount,
            collectionAudioStatsLoading,
            collectionClientMonitoredAudioRelationsLoading,
            errorLoadingCollectionAudioStats,
            ordering,
            page,
            pageSize,
            rows,
            search,
        ]
    );

    const utils: CollectionAudiosUtils = useMemo(
        () => ({
            refresh,
            setPage: (page: number) => setUrlState({ aP: page }),
            setPageSize: (pageSize: number | undefined) => setUrlState({ aPS: pageSize }),
            setSearch: (query: string) => setUrlState({ aS: query, aP: 1 }),
            setOrdering: (order: GetTiktokAudioCollectionAudioStatsParams['ordering']) => setUrlState({ aO: order }),
            setAudioIdForModal: (id: number | undefined) => setUrlState({ audioModal: id }),
            setAlternateName: (rel: ClientMonitoredTiktokAudioRelationship) =>
                dispatch({ type: 'setClientMonitoredAudioRelation', payload: rel }),
        }),
        [dispatch, refresh, setUrlState]
    );
    return [values, utils];
};

export default useCollectionAudios;
